import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry for error monitoring
 * Simple initialization as per official documentation
 */
export const initSentry = (): void => {
  const release = process.env.REACT_APP_SENTRY_RELEASE;
  const environment = process.env.REACT_APP_SENTRY_ENV;
  // Skip Sentry completely in local development
  if (!environment) {
    console.log('[Sentry] Skipped — no REACT_APP_SENTRY_ENV (local development)');
    return;
  }
  Sentry.init({
    dsn:
      'https://557bc08485080ea706372afb0f47f575@o4508891487272960.ingest.de.sentry.io/4509743957737552',
    release,
    environment,
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    integrations: [Sentry.browserTracingIntegration()],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
  });
};

/**
 * Sets user in Sentry
 */
export const setSentryUser = (userId: string, email?: string): void => {
  try {
    Sentry.setUser({
      id: userId,
      email: email,
    });
  } catch (error) {
    // Sentry not initialized, skip silently
  }
};

/**
 * Adds context to Sentry
 */
export const setSentryContext = (key: string, context: Record<string, any>): void => {
  try {
    Sentry.setContext(key, context);
  } catch (error) {
    // Sentry not initialized, skip silently
  }
};

/**
 * Adds tag to Sentry
 */
export const setSentryTag = (key: string, value: string): void => {
  try {
    Sentry.setTag(key, value);
  } catch (error) {
    // Sentry not initialized, skip silently
  }
};

/**
 * Manual error sending to Sentry
 */
export const captureSentryException = (error: Error): void => {
  try {
    Sentry.captureException(error);
  } catch (sentryError) {
    // Sentry not initialized, skip silently
  }
};

/**
 * Manual message sending to Sentry
 */
export const captureSentryMessage = (
  message: string,
  level: Sentry.SeverityLevel = 'info'
): void => {
  try {
    Sentry.captureMessage(message, level);
  } catch (sentryError) {
    // Sentry not initialized, skip silently
  }
};

export default {
  initSentry,
  setSentryUser,
  setSentryContext,
  setSentryTag,
  captureSentryException,
  captureSentryMessage,
};
